import {
  type CompletionGroup,
  PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL,
  type PrescriptionTransferReviewStatus,
} from "@care-by-ella/contracts";

// Swedish labels for the state machines the clinician surfaces render.
//
// The portal is a Swedish clinical tool, but several screens printed the raw
// enum: the patient page said "Behandlingstillstånd: onboarding", the meeting
// prep said "Resa: labs_required" and "Arbetsläge: scheduled" — beside columns
// that WERE translated, in the same table. The patient list had the only
// Swedish treatment vocabulary and kept it private to that file.
//
// Each lookup falls back to the raw key, so a state added server-side shows up
// as an obviously-unlabelled string rather than blank or, worse, wrong.

const TREATMENT_STATE_LABELS: Record<string, string> = {
  no_treatment: "Ingen behandling",
  onboarding: "Onboarding",
  active: "Aktiv",
  paused: "Pausad",
  ended: "Avslutad",
};

export function treatmentStateLabel(state: string): string {
  return TREATMENT_STATE_LABELS[state] ?? state;
}

// The patient-journey ladder (`JOURNEY_STATES`). Clinician vocabulary — this is
// the staff portal, not patient copy.
const JOURNEY_STATE_LABELS: Record<string, string> = {
  lead_started: "Påbörjad ansökan",
  eligibility_completed: "F-01 besvarad",
  meeting_booked: "Vårdmöte bokat",
  health_declaration_completed: "Hälsodeklaration inskickad",
  prescription_transfer_review_pending: "Receptövertag att granska",
  doctor_visit_completed: "Vårdmöte genomfört",
  labs_required: "Blodprov krävs",
  labs_completed: "Provsvar inkomna",
  treatment_decision_ready: "Redo för behandlingsbeslut",
  treatment_started: "Behandling startad",
  active_treatment: "Aktiv behandling",
  maintenance: "Underhåll",
  paused: "Pausad",
  ended: "Avslutad",
};

// TES-1034 S5 — an approved prescription transfer also lands on
// doctor_visit_completed (the home screen's approved-transfer card and the
// journey ladder key off it), but no care meeting was held. For her the
// state reads as what happened; in a count across patients, as both.
export const TRANSFER_APPROVED_JOURNEY_LABEL = "Receptövertag godkänt";
const DOCTOR_VISIT_OR_TRANSFER_LABEL =
  "Vårdmöte genomfört / receptövertag godkänt";

export function journeyStateLabel(
  state: string,
  opts: { transferApproved?: boolean; acrossPatients?: boolean } = {},
): string {
  if (state === "doctor_visit_completed") {
    if (opts.transferApproved) return TRANSFER_APPROVED_JOURNEY_LABEL;
    if (opts.acrossPatients) return DOCTOR_VISIT_OR_TRANSFER_LABEL;
  }
  return JOURNEY_STATE_LABELS[state] ?? state;
}

// The meeting WORKFLOW ladder (consult → documentation), orthogonal to the
// scheduling status shown in the meeting list.
//
// TES-766 — `prepped` is gone from the ladder. The label "Förberedd" lingered
// here for a state no code ever set; a label for a state that does not exist
// is exactly the kind of fossil the clean-up is about. The type is
// `Record<string, string>` and not exhaustive over `MeetingWorkflowStatus`,
// so no type check would have caught it.
const MEETING_WORKFLOW_LABELS: Record<string, string> = {
  scheduled: "Ej påbörjad",
  in_progress: "Pågår",
  documented: "Dokumenterad",
};

export function meetingWorkflowLabel(status: string): string {
  return MEETING_WORKFLOW_LABELS[status] ?? status;
}

// TES-458 — the prescription-transfer review (`PRESCRIPTION_TRANSFER_REVIEW_STATUSES`).
// One vocabulary for the inbox chip and the case view, with the tone the
// portal already uses for state: open states warn, terminals conclude.
const TRANSFER_REVIEW_STATUS_LABELS: Record<
  PrescriptionTransferReviewStatus,
  { label: string; className: string }
> = {
  // TES-734 — Emelie's queue words (A-02):
  // "att granska · komplettering krävs · nytt blodprov krävs · godkänt · kan inte tas över"
  pending_review: {
    label: "Att granska",
    className: "bg-warning/15 text-warning border-warning/60",
  },
  // Legacy (rows decided before 2026-09-17); no new decision produces it.
  meeting_required: {
    label: "Vårdmöte krävs",
    className: "bg-warning/15 text-warning border-warning/60",
  },
  completion_requested: {
    label: "Komplettering krävs",
    className: "bg-warning/15 text-warning border-warning/60",
  },
  labs_required: {
    label: "Nytt blodprov krävs",
    className: "bg-warning/15 text-warning border-warning/60",
  },
  approved: {
    label: "Godkänt övertag",
    className: "bg-success/15 text-success border-success/60",
  },
  cannot_transfer: {
    label: "Kan inte tas över",
    className: "bg-danger/10 text-danger border-danger/60",
  },
};

export function transferReviewStatusLabel(
  status: PrescriptionTransferReviewStatus,
  // TES-1034 S5 — approved, and her subscription has not yet activated the
  // first cycle: the same words as the header's clinical phase.
  opts: { awaitingSubscription?: boolean } = {},
): { label: string; className: string } {
  if (status === "approved" && opts.awaitingSubscription) {
    return {
      ...TRANSFER_REVIEW_STATUS_LABELS.approved,
      label: AWAITING_SUBSCRIPTION_LABEL,
    };
  }
  return (
    TRANSFER_REVIEW_STATUS_LABELS[status] ?? {
      label: status,
      className: "bg-surfaceMuted/40 text-textSecondary border-surfaceMuted",
    }
  );
}

// What the doctor can ask her to redo (`COMPLETION_GROUPS`). Two are F-07
// sections by id; `other_medical` is anything outside the form (F-02, free
// text) and goes through the chat rather than a reopened section.
export const COMPLETION_GROUP_LABELS: Record<CompletionGroup, string> = {
  current_treatment: "Läkemedelsuppgifter",
  prescription_evidence: "Receptunderlag",
  other_medical: "Annan medicinsk komplettering",
};

// TES-761 — groups that existed before F-07 v4 and can still appear on stored
// rows (`requestedCompletionGroups` is a plain string[]). They are no longer
// requestable, but a historical case must not render its own history as a raw
// key. Kept separate so the typed record above stays exhaustive over the LIVE
// groups and a new one still has to be labelled deliberately.
const RETIRED_COMPLETION_GROUP_LABELS: Record<string, string> = {
  blood_pressure_pulse: "Blodtryck och puls",
  prior_labs: "Tidigare provsvar",
};

export function completionGroupLabel(group: string): string {
  return (
    COMPLETION_GROUP_LABELS[group as CompletionGroup] ??
    RETIRED_COMPLETION_GROUP_LABELS[group] ??
    group
  );
}

// TES-1034 (Emelie, point 7) — internal values such as "medium" and
// "pending_assessment" were printed raw in the case view and on the patient
// page. Same rule as above: Swedish label, raw key only as a fallback.
const CYCLE_STATE_LABELS: Record<string, string> = {
  pending_assessment: "Väntar på bedömning",
  active: "Aktiv",
  expiring_soon: "Löper snart ut",
  renewal_pending: "Förnyelse väntar",
  doctor_review: "Läkargranskning",
  approved: "Godkänd",
  rejected: "Avslagen",
  paused: "Pausad",
  discontinued: "Avslutad",
};

// TES-1034 S5 — the header's wording for a positive decision whose first
// cycle awaits activation (CLINICAL_STAGE_LABEL.approved_awaiting_subscription).
// TES-1141: "Inväntar aktiverat abonnemang", for a new treatment too.
// TES-1150: that stage now begins with the doctor's registration of the
// prescription, so it reads "Recept utfärdat – inväntar abonnemangsaktivering".
export const AWAITING_SUBSCRIPTION_LABEL =
  PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL;

export function cycleStateLabel(
  state: string,
  opts: { awaitingSubscription?: boolean } = {},
): string {
  if (state === "pending_assessment" && opts.awaitingSubscription) {
    return AWAITING_SUBSCRIPTION_LABEL;
  }
  return CYCLE_STATE_LABELS[state] ?? state;
}

// A flag's severity (an evaluator's sort key) and a handoff's priority share
// one four-step vocabulary.
const LEVEL_LABELS: Record<string, string> = {
  urgent: "akut",
  high: "hög",
  medium: "medel",
  low: "låg",
};

export function levelLabel(level: string): string {
  return LEVEL_LABELS[level] ?? level;
}

// Where a flag escalates. "none" is not shown at all by the callers.
const ESCALATION_LABELS: Record<string, string> = {
  doctor: "till läkare",
  urgent: "akut",
};

export function escalationLabel(escalate: string): string {
  return ESCALATION_LABELS[escalate] ?? escalate;
}

// F-07's `pt_remaining_supply` option ids (REMAINING_SUPPLY in the API's
// transfer-gap-risk.ts). The queue printed "over_4_veckor" raw because its map
// still held only the v3 dose counts; the case view fell through to "not
// given" for the same answer. The v3 ids stay: older submissions carry them.
// The words are the form's own option labels, shortened where the question
// ("how long will it last") is not on screen.
const REMAINING_SUPPLY_LABELS: Record<string, string> = {
  // v4+ — how long it lasts (Ö-153a).
  inget_kvar: "Inget kvar",
  under_1_vecka: "Mindre än 1 vecka",
  "1_2_veckor": "1–2 veckor",
  "3_4_veckor": "3–4 veckor",
  over_4_veckor: "Mer än 4 veckor",
  vet_inte: "Vet inte",
  // v3 — dose counts.
  ingen: "Inga doser",
  en_dos: "En dos",
  tva_tre: "2–3 doser",
  fyra_plus: "4 doser eller fler",
  osaker: "Osäker",
};

export function remainingSupplyLabel(value: string): string {
  return REMAINING_SUPPLY_LABELS[value] ?? value;
}
